import type { IdlingSettings, IdlingVehicleDecorated } from './idling-types';
import { CURR_SYMBOLS } from './idling-types';
import { fmtCost, fmtHrs, unitLabel, idlingSortCol, idlingSortAsc, setIdlingSortCol, setIdlingSortAsc, recalculateAndRender } from './idling-page';
import { escapeHtml } from '../utils/html';

// ── Sortable on-page table ──────────────────────────────────────────────────
// The XLSX report lives in ./idling-export-xlsx.ts (lazy-loaded on download).

export function renderIdlingTable(
  s: IdlingSettings,
  perVehicle: IdlingVehicleDecorated[],
  sortCol: string,
  sortAsc: boolean
): void {
  const sym = CURR_SYMBOLS[s.currency] ?? '$';
  const ul  = unitLabel(s.unit);

  type ColKey = keyof IdlingVehicleDecorated;
  const cols: { key: ColKey; label: string; fmt: (v: IdlingVehicleDecorated) => string }[] = [
    { key: 'name',           label: 'Vehicle',             fmt: (v) => escapeHtml(v.name) },
    { key: 'groupsLabel',    label: 'Group',               fmt: (v) => `<span class="td-muted">${escapeHtml(v.groupsLabel)}</span>` },
    { key: 'eventCount',     label: 'Idle Events',         fmt: (v) => `<span class="td-mono">${v.eventCount}</span>` },
    { key: 'idleHrs',        label: 'Idle Time',           fmt: (v) => `<span class="td-mono">${fmtHrs(v.idleHrs)}</span>` },
    { key: 'fuelVol',        label: `Fuel (${ul})`,        fmt: (v) => `<span class="td-mono">${v.fuelVol.toFixed(2)} ${ul}</span>` },
    { key: 'cost',           label: `Cost (${s.currency})`,fmt: (v) => `<span class="td-mono td-warn">${fmtCost(v.cost, sym)}</span>` },
    { key: 'longestIdleHrs', label: 'Longest Event',       fmt: (v) => `<span class="td-mono">${fmtHrs(v.longestIdleHrs)}</span>` },
  ];

  const sorted = [...perVehicle].sort((a, b) => {
    const av = a[sortCol as ColKey] as number | string;
    const bv = b[sortCol as ColKey] as number | string;
    const dir = sortAsc ? 1 : -1;
    return typeof av === 'string' ? (av as string).localeCompare(bv as string) * dir : ((av as number) - (bv as number)) * dir;
  });

  const thHTML = cols.map((c) => {
    const active = c.key === sortCol;
    const arrow  = active ? (sortAsc ? '▲' : '▼') : '▲';
    return `<th class="${active ? 'sort-active' : ''}" onclick="window.__idlingSortBy('${c.key}')">${c.label}<span class="sort-arrow">${arrow}</span></th>`;
  }).join('');

  const rowsHTML = sorted.map((v, i) => {
    const cls = i === 0 ? 'td-rank1' : i === 1 ? 'td-rank2' : i === 2 ? 'td-rank3' : '';
    return `<tr>${cols.map((c) => `<td class="${cls}">${c.fmt(v)}</td>`).join('')}</tr>`;
  }).join('');

  (document.getElementById('idlingTable') as HTMLElement).innerHTML =
    `<thead><tr>${thHTML}</tr></thead><tbody>${rowsHTML}</tbody>`;
  (document.getElementById('idlingRowCount') as HTMLElement).textContent =
    sorted.length + ' vehicle' + (sorted.length !== 1 ? 's' : '');

  // Register global sort handler so inline onclick can reach it
  (window as Window & { __idlingSortBy?: (col: string) => void }).__idlingSortBy = (col: string) => {
    if (idlingSortCol === col) setIdlingSortAsc(!idlingSortAsc);
    else { setIdlingSortCol(col); setIdlingSortAsc(false); }
    recalculateAndRender();
  };
}
